/**
 * AtlasAreaLightsModule — LTC rect area lights on the lit windows at night (shader additions wave, 2026-09-15);
 * THE FACING, ROOM and OCCLUDER rules (shader gaps round 6, agent J, 2026-09-30).
 *
 * The engine already carries three's production LTC path (`initLTC` uploads the two 64² LUTs so `RectAreaLightNode` is
 * not black). This module keeps a small POOL of rect lights and seats them on the nearest glass panes as rectangular
 * emitters whose radiance follows the night and the lit-window ratio (`areaLightIntensity`). A rectangle's polygon
 * integral already accounts for its solid angle and distance; no point-light falloff is layered on top.
 *
 * THE LIGHT-SET IDENTITY LAW applies: the pool is created at init (only when the flag is on — the menu row says
 * "reload"), every light stays permanently visible, an unused slot is intensity 0 parked at y = −10000. Only uniforms
 * and transforms change per frame (the room mask and the occluders are uniforms of each pooled light).
 *
 * WHAT CHANGED (2026-09-30, `lib/pane_visibility.ts` has the rules and their CPU mirrors, `lib/pane_light_node.ts` the
 * shader). The emitter used to face "the pane's thinnest axis, flipped toward the viewer": a pane on the FAR side of a
 * building turned round and lit the street on the player's side THROUGH the building, and the active interior's panes
 * lit the ground outside the opposite wall. Now:
 *   · THE FACING LAW — a pane faces where its light comes from: an INTERIOR pane (the active interior's own glass) faces
 *     into its room, decided by the building's centre; an EXTERIOR pane faces out of its building, decided by the
 *     glass's own authored front (the building lies behind it). A pane with no readable front (a many-sided glass set,
 *     a lamp's globe) is left unseated, never guessed. three's LTC refuses everything behind the emitter's plane, so
 *     the right facing IS "lights only the side it faces".
 *   · THE ROOM LAW — an interior pane's light is confined to its OWN building's box (`setWindowRoom`, the shell's
 *     half extents + a pad for the wall faces): the ground outside the opposite wall stays dark.
 *   · THE OCCLUDER TERM (`windowShadows`, high / ultra; `atlas:pane-shadows = 'off'`) — up to four people near each
 *     of the two NEAREST panes (`PANE_SHADOW_SLOTS`; the player, other travelers, walking versonas) as vertical
 *     capsules; the light's node takes four stratified samples over the rectangle and scales the unshadowed LTC by the
 *     ratio estimate (never one centre ray, never a spot light on top), only for receivers within 20 m of the pane.
 * Instanced glass (the settlements' StaticInstancer batches) is seated PER INSTANCE (batch matrix × instance matrix),
 * never at the batch's origin (the old scan did).
 *
 * Kill switch: localStorage `atlas:pane-light = 'legacy'` restores the old pool (plain rect lights turned toward the
 * viewer, no mask, no occluders).
 *
 * FRAME: pane positions are read off `matrixWorld` (render space); the lights are scene children (absolute) —
 * `absolute = render − scene.position`. Enterables, the player and the people are absolute.
 *
 * Lever: `__areaLights()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { initLTC } from 'enginex/render/lighting/area/ltcRuntime.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { uNightFactor } from './SkyModule.js';
import { activePreset } from '../lib/quality_tiers.js';
import { isGlassMaterialName, litRatioForNight } from '../lib/stage3_fx.js';
import { AREA_LIGHT_POOL, AREA_LIGHT_RANGE_M, areaLightIntensity, stage5Stats } from '../lib/stage5_fx.js';
import { emissiveEvScale } from '../lib/photometry.js';
import { paneRole, paneFacing, paneFrontFacing, pickOccluders, personCapsule, roomBoxFor, PANE_OCCLUDER_RANGE_M, PANE_SHADOW_SLOTS, type PaneRole, type V3, type PaneBox } from '../lib/pane_visibility.js';
import { WindowAreaLight, registerWindowLightNode, seatWindowLight, setWindowRoom, setWindowOccluders } from '../lib/pane_light_node.js';
import { enterables, interiorSpec, FLOOR_H } from '../lib/building_interiors.js';

/** The interior a pane belongs to: the nearest ancestor tagged by BuildingInteriorModule, else null (an exterior pane). */
function interiorIdOf(o: THREE.Object3D): string | null {
  for (let p: THREE.Object3D | null = o; p; p = p.parent) { const id = (p.userData as { interiorId?: unknown }).interiorId; if (typeof id === 'string') return id; }
  return null;
}

/**
 * A glass pane's material name. Interior mapping renames a structure's glass `<name>#interior` (and the crossfade
 * clones add `#fade`) — `isGlassMaterialName` refuses a '#' so the mapping never runs twice; the pane itself is still
 * glass, so the scan reads the name before the first '#'.
 */
function paneName(name: string | undefined): boolean {
  if (!name) return false;
  const base = name.split('#')[0];
  return isGlassMaterialName(base);
}

const PARK_Y = -10000;
const SCAN_EVERY = 45;
const OCCLUDE_EVERY = 2;
const COLOR = 0xffd9a3;
/** A pane's authored front is readable when its mean vertex normal is at least this long (a planar pane ≈ 1). */
const PLANAR_MIN = 0.7;
/** …and when it agrees with the pane's thin axis by at least this much. */
const FRONT_MIN = 0.5;
/** Instances of one glass batch tested per scan (a city's batch is a few hundred; the bound keeps a scan flat). */
const MAX_INSTANCES_PER_BATCH = 4096;

type Mode = 'window' | 'plain' | 'legacy';

interface GeoInfo { cx: number; cy: number; cz: number; ex: number; ey: number; ez: number; mean: V3 | null }
const geoInfo = new WeakMap<THREE.BufferGeometry, GeoInfo>();
function infoOf(g: THREE.BufferGeometry): GeoInfo {
  const hit = geoInfo.get(g);
  if (hit) return hit;
  if (!g.boundingBox) g.computeBoundingBox();
  const bb = g.boundingBox!;
  let mean: V3 | null = null;
  const nrm = g.getAttribute('normal') as THREE.BufferAttribute | undefined;
  if (nrm && nrm.count > 0) {
    let sx = 0, sy = 0, sz = 0;
    for (let i = 0; i < nrm.count; i++) { sx += nrm.getX(i); sy += nrm.getY(i); sz += nrm.getZ(i); }
    const L = Math.hypot(sx, sy, sz);
    if (L / nrm.count >= PLANAR_MIN) mean = [sx / L, sy / L, sz / L];
  }
  const info: GeoInfo = {
    cx: (bb.min.x + bb.max.x) / 2, cy: (bb.min.y + bb.max.y) / 2, cz: (bb.min.z + bb.max.z) / 2,
    ex: bb.max.x - bb.min.x, ey: bb.max.y - bb.min.y, ez: bb.max.z - bb.min.z, mean,
  };
  geoInfo.set(g, info);
  return info;
}

/** A seated candidate: centre + facing in RENDER space, the rectangle's size, its role and (interior) the room. */
interface Pane { x: number; y: number; z: number; n: V3; w: number; h: number; d2: number; role: PaneRole; room: PaneBox | null; interiorId: string | null }

export class AtlasAreaLightsModule implements IModule {
  readonly name = 'AreaLights';
  private app!: App;
  private readonly lights: THREE.RectAreaLight[] = [];
  private mode: Mode = 'window';
  private shadows = false;
  private frame = 0;
  private panes: Pane[] = [];
  private readonly stats = { candidates: 0, seated: 0, instanced: 0, unfaced: 0, outOfRole: 0, occluders: 0 };
  private readonly _mw = new THREE.Matrix4();
  private readonly _mi = new THREE.Matrix4();
  private readonly _c = new THREE.Vector3();
  private readonly _a = new THREE.Vector3();
  private readonly _e = new THREE.Vector3();
  private readonly _m = new THREE.Vector3();
  private readonly _n3 = new THREE.Matrix3();

  init(app: App): void {
    this.app = app;
    const p = activePreset();
    if (!p.areaLights) return; // the pool's existence is fixed for the session
    let legacy = false, shadowsOff = false;
    try { legacy = localStorage.getItem('atlas:pane-light') === 'legacy'; shadowsOff = localStorage.getItem('atlas:pane-shadows') === 'off'; } catch { /* storage blocked */ }
    try { initLTC(); } catch { /* LTC LUTs unavailable: the lights render black, nothing else breaks */ }
    // THE NODE FIRST: three keys its light-node library by the light's constructor, so the subclass's node must be
    // registered before any WindowAreaLight joins the scene (an unregistered subclass would be skipped with a warning).
    const registered = !legacy && registerWindowLightNode(app.renderer.native);
    this.mode = legacy ? 'legacy' : registered ? 'window' : 'plain';
    this.shadows = this.mode === 'window' && p.windowShadows === true && !shadowsOff;
    for (let i = 0; i < AREA_LIGHT_POOL; i++) {
      // the occluder term on the NEAREST panes' slots only (the scan seats in distance order) — its code is compiled into every lit program
      const l = this.mode === 'window' ? new WindowAreaLight(COLOR, 0, 2, 3, this.shadows && i < PANE_SHADOW_SLOTS) : new THREE.RectAreaLight(COLOR, 0, 2, 3);
      l.position.set(0, PARK_Y, 0);
      app.world.scene.add(l);
      this.lights.push(l);
    }
    app.world.addSystem(makeSystem('AtlasAreaLights', Phase.RenderSub, 11, () => this.tick()));
    // probe lever: scan + occluders NOW (the scan's 45-frame cadence is minutes at a SwiftShader city's frame rate)
    (globalThis as unknown as { __areaLightsScan?: () => void }).__areaLightsScan = () => { this.scan(); if (this.shadows) this.occlude(); };
    (globalThis as unknown as { __areaLights?: () => unknown }).__areaLights = () => ({
      mode: this.mode,
      shadows: this.shadows,
      pool: this.lights.length,
      seated: this.lights.filter((l) => l.position.y > PARK_Y + 1).length,
      panes: this.panes.length,
      intensity: stage5Stats.areaLightIntensity,
      stats: { ...this.stats },
      lights: this.lights.map((l, i) => {
        const pane = this.panes[i];
        const wl = l as Partial<WindowAreaLight>;
        return {
          x: +l.position.x.toFixed(1), y: +l.position.y.toFixed(1), z: +l.position.z.toFixed(1), i: +l.intensity.toFixed(2), w: +l.width.toFixed(2), h: +l.height.toFixed(2),
          role: pane?.role ?? null, interiorId: pane?.interiorId ?? null, n: pane ? pane.n.map((v) => +v.toFixed(3)) : null,
          room: wl.maskMode ?? 0, occluders: wl.occluderCount ?? 0,
        };
      }),
    });
  }

  /** The room of the active interior (absolute frame), or null. */
  private roomOf(interiorId: string | null): { box: PaneBox; centre: V3 } | null {
    if (!interiorId) return null;
    const b = enterables.get(interiorId);
    if (!b) return null;
    const spec = interiorSpec(b.w, b.h, b.d, b.key);
    const height = spec.floors * FLOOR_H;
    return { box: roomBoxFor(b.x, b.z, b.yaw, b.groundY, spec.hw, spec.hd, height), centre: [b.x, b.groundY + height / 2, b.z] };
  }

  /**
   * One pane (a mesh, or one instance of a batch) → a candidate or nothing. `W` is its world matrix in RENDER space.
   * The rectangle's three edge vectors come from the geometry's extents through the full matrix (scale and rotation
   * together — an instance's scale is honoured); the thin one is the pane's axis, found as the cross of the two wide
   * edges so a zero-thickness plane still has one.
   */
  private candidate(W: THREE.Matrix4, info: GeoInfo, role: PaneRole, interiorId: string | null, room: { box: PaneBox; centre: V3 } | null, px: number, py: number, pz: number, sp: THREE.Vector3, r2: number, found: Pane[]): void {
    this._c.set(info.cx, info.cy, info.cz).applyMatrix4(W);
    const dx = this._c.x - px, dy = this._c.y - py, dz = this._c.z - pz;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 > r2) return;
    this.stats.candidates++;
    this._n3.setFromMatrix4(W);
    const ex = this._a.set(info.ex, 0, 0).applyMatrix3(this._n3).clone();
    const ey = this._a.set(0, info.ey, 0).applyMatrix3(this._n3).clone();
    const ez = this._a.set(0, 0, info.ez).applyMatrix3(this._n3).clone();
    const edges = [ex, ey, ez].sort((u, v) => u.length() - v.length());
    const wide1 = edges[1], wide2 = edges[2];
    const axis = this._e.crossVectors(wide1, wide2);
    if (axis.lengthSq() < 1e-10) { this.stats.unfaced++; return; }
    axis.normalize();
    const pane: V3 = [this._c.x, this._c.y, this._c.z];
    const ax: V3 = [axis.x, axis.y, axis.z];
    let n: V3 | null = null;
    if (this.mode === 'legacy') {
      // the pre-2026-09-30 rule, kept only behind the kill switch: turned toward the viewer
      const s = ax[0] * (px - pane[0]) + ax[1] * (py - pane[1]) + ax[2] * (pz - pane[2]) >= 0 ? 1 : -1;
      n = [ax[0] * s, ax[1] * s, ax[2] * s];
    } else if (role === 'interior') {
      if (!room) { this.stats.unfaced++; return; }
      const centre: V3 = [room.centre[0] + sp.x, room.centre[1] + sp.y, room.centre[2] + sp.z]; // absolute → render
      n = paneFacing(pane, ax, centre, 'interior');
    } else if (info.mean) {
      // THE FACING LAW outdoors: the glass's authored front points out of its building, so the building lies behind it
      this._m.set(info.mean[0], info.mean[1], info.mean[2]).applyMatrix3(this._n3).normalize();
      n = paneFrontFacing(pane, ax, [this._m.x, this._m.y, this._m.z], FRONT_MIN);
    }
    if (!n) { this.stats.unfaced++; return; }
    // width = the more horizontal wide edge (the light's local x), height = the more vertical one (its local y)
    const [wv, hv] = Math.abs(wide1.y) / (wide1.length() || 1) > Math.abs(wide2.y) / (wide2.length() || 1) ? [wide2, wide1] : [wide1, wide2];
    found.push({ x: pane[0], y: pane[1], z: pane[2], n, w: Math.max(0.5, Math.min(6, wv.length())), h: Math.max(0.5, Math.min(6, hv.length())), d2, role, room: role === 'interior' ? room!.box : null, interiorId });
  }

  private scan(): void {
    const scene = this.app.world.scene;
    const sp = scene.position;
    const px = atlas.localX + sp.x, py = atlas.localY + sp.y, pz = atlas.localZ + sp.z; // the player in RENDER space
    const found: Pane[] = [];
    const r2 = AREA_LIGHT_RANGE_M * AREA_LIGHT_RANGE_M;
    const active = atlas.interiorId ?? null;
    const room = this.roomOf(active);
    this.stats.candidates = 0; this.stats.instanced = 0; this.stats.unfaced = 0; this.stats.outOfRole = 0;
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.visible || found.length >= 64) return;
      const mat = (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.Material | undefined;
      if (!mat || !paneName(mat.name)) return;
      const role = this.mode === 'legacy' ? (active === null ? 'exterior' : interiorIdOf(m) === active ? 'interior' : null) : paneRole(interiorIdOf(m), active);
      if (role === null) { this.stats.outOfRole++; return; }
      const info = infoOf(m.geometry);
      const inst = m as unknown as THREE.InstancedMesh;
      if (inst.isInstancedMesh) {
        // PER INSTANCE: the batch's matrix × each instance's own (never the batch's origin). A city's glass batches hold
        // thousands of instances: the range test reads each instance's translation straight from the array through the
        // batch matrix (no Matrix4 copy), and only an instance in reach is composed and measured.
        const arr = inst.instanceMatrix.array as ArrayLike<number>, W = m.matrixWorld.elements;
        const n = Math.min(inst.count, MAX_INSTANCES_PER_BATCH);
        const cx = info.cx, cy = info.cy, cz = info.cz;
        for (let i = 0; i < n && found.length < 64; i++) {
          const o = i * 16;
          if (arr[o] === 0 && arr[o + 5] === 0 && arr[o + 10] === 0) continue; // a dead / zero-scale slot
          // the GLASS's centre through the instance (a cooked civ model's origin may sit hundreds of metres from its glass — the PIVOT LAW)
          const tx = arr[o] * cx + arr[o + 4] * cy + arr[o + 8] * cz + arr[o + 12];
          const ty = arr[o + 1] * cx + arr[o + 5] * cy + arr[o + 9] * cz + arr[o + 13];
          const tz = arr[o + 2] * cx + arr[o + 6] * cy + arr[o + 10] * cz + arr[o + 14];
          const wx = W[0] * tx + W[4] * ty + W[8] * tz + W[12] - px, wy = W[1] * tx + W[5] * ty + W[9] * tz + W[13] - py, wz = W[2] * tx + W[6] * ty + W[10] * tz + W[14] - pz;
          this.stats.instanced++;
          if (wx * wx + wy * wy + wz * wz > r2) continue;
          this._mi.fromArray(arr as number[], o);
          this._mw.multiplyMatrices(m.matrixWorld, this._mi);
          this.candidate(this._mw, info, role, interiorIdOf(m), role === 'interior' ? room : null, px, py, pz, sp, r2, found);
        }
        return;
      }
      this.candidate(m.matrixWorld, info, role, interiorIdOf(m), role === 'interior' ? room : null, px, py, pz, sp, r2, found);
    });
    found.sort((a, b) => a.d2 - b.d2);
    this.panes = found.slice(0, AREA_LIGHT_POOL);
    // Seat the pool (absolute = render − scene.position), 5 cm off the pane, emitting along its facing.
    for (let i = 0; i < this.lights.length; i++) {
      const l = this.lights[i], pane = this.panes[i];
      if (!pane) { l.position.set(0, PARK_Y, 0); if (l instanceof WindowAreaLight) { setWindowRoom(l, null); l.occluderCount = 0; } continue; }
      seatWindowLight(l, [pane.x - sp.x, pane.y - sp.y, pane.z - sp.z], pane.n, pane.w, pane.h);
      if (l instanceof WindowAreaLight) setWindowRoom(l, pane.room); // THE ROOM LAW: an interior pane lights only its own building
    }
    this.stats.seated = this.panes.length;
    stage5Stats.areaLightsSeated = this.panes.length;
  }

  /** The people who may stand between a pane and what it lights (absolute feet positions). */
  private people(): Array<{ x: number; y: number; z: number }> {
    const out: Array<{ x: number; y: number; z: number }> = [{ x: atlas.localX, y: atlas.localY, z: atlas.localZ }];
    for (const r of atlas.remotes.values()) {
      const o = r.mesh;
      if (o && o.visible) out.push({ x: o.position.x, y: o.position.y, z: o.position.z });
    }
    const env = this.app.getModule('Environment') as unknown as { walkers?: (x: number, z: number, r: number) => Array<{ x: number; y: number; z: number }> } | undefined;
    const walkers = env?.walkers?.(atlas.localX, atlas.localZ, AREA_LIGHT_RANGE_M + PANE_OCCLUDER_RANGE_M) ?? [];
    for (const w of walkers) out.push({ x: w.x, y: w.y, z: w.z });
    return out;
  }

  private occlude(): void {
    const people = this.people();
    let total = 0;
    for (let i = 0; i < this.lights.length; i++) {
      const l = this.lights[i];
      if (!(l instanceof WindowAreaLight) || !l.visibility) continue;
      if (!this.panes[i]) { l.occluderCount = 0; continue; }
      const near = pickOccluders(l.position.x, l.position.z, people);
      setWindowOccluders(l, near.map((p) => personCapsule(p.x, p.y, p.z)));
      total += l.occluderCount;
    }
    this.stats.occluders = total;
  }

  private tick(): void {
    if (!this.lights.length) return;
    this.frame++;
    const on = !!activePreset().areaLights && !atlas.spaceFlight && atlas.currentWorldId != null;
    if (!on) { for (const l of this.lights) l.intensity = 0; return; }
    if (this.frame % SCAN_EVERY === 0) this.scan();
    if (this.shadows && this.frame % OCCLUDE_EVERY === 0) this.occlude();
    const night = (uNightFactor as unknown as { value: number }).value ?? 0;
    // EMISSIVES IN EV (2026-09-25, §10 R9): a window's radiance is display-anchored — it reads the same under the
    // metered exposure correction (× exposureRef / exposure), like the lit rooms behind it.
    const intensity = areaLightIntensity(night, litRatioForNight(night)) * emissiveEvScale(0);
    stage5Stats.areaLightIntensity = intensity;
    for (let i = 0; i < this.lights.length; i++) this.lights[i].intensity = this.panes[i] ? intensity : 0;
  }
}
